import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Spinner, Card, Button, Collapse, Row, Col, Badge, Container, Form, Modal } from "react-bootstrap";
import { FaUserFriends, FaChevronDown, FaChevronUp } from "react-icons/fa";
import * as XLSX from "xlsx";
import axios from "./utils/axiosConfig";
import { jwtDecode } from 'jwt-decode';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';

const PjoinPage = () => {
  const [activities, setActivities] = useState([]);
  const [participationStatus, setParticipationStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const location = useLocation();

  // 用於顯示問題的狀態
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [activityQuestions, setActivityQuestions] = useState([]);
  const [selectedActivityForQuestions, setSelectedActivityForQuestions] = useState(null);
  const [responses, setResponses] = useState({});
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  // 快取用戶資訊
  const userInfo = useMemo(() => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken;
    } catch (error) {
      return null;
    }
  }, []);

  // 優化的 API 調用
  const fetchActivities = useCallback(async () => {
    if (!userInfo) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      // 並行獲取活動資料和參與狀態
      const [activitiesResponse, statusResponse] = await Promise.all([
        axios.get("/activities", {
          params: { e_ssid: userInfo.e_ssid },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("/participation-status", {
          params: { e_id: userInfo.e_id },
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);

      const activitiesData = activitiesResponse.data;
      const statusData = statusResponse.data;

      console.log('Pjoinpage: 收到活動數據', activitiesData?.length || 0, '筆');
      console.log('Pjoinpage: userInfo.e_ssid =', userInfo?.e_ssid);

      // 前端時間過濾近一個月內的活動 (包含前24小時)
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000); // 前24小時
      const sixMonthsLater = new Date();
      sixMonthsLater.setMonth(now.getMonth() + 6);

      const filteredActivities = activitiesData.filter((activity) => {
        const activityStartDate = new Date(activity.a_Sdate);
        const isInRange = activityStartDate >= twentyFourHoursAgo && activityStartDate <= sixMonthsLater;
        return isInRange;
      });

      console.log('Pjoinpage: 過濾後活動數量', filteredActivities.length, '筆');

      // 按開始日期排序
      const sortedActivities = filteredActivities.sort(
        (a, b) => new Date(a.a_Sdate) - new Date(b.a_Sdate)
      );

      // 建立狀態對應
      const statusMap = statusData.reduce((acc, cur) => {
        acc[cur.activity_id] = cur.status;
        return acc;
      }, {});

      setActivities(sortedActivities);
      setParticipationStatus(statusMap);
       
    } catch (error) {
      console.error('Pjoinpage API 錯誤:', error);
      console.error('錯誤詳情:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  }, [userInfo]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities, location.search]);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(activities);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Activities");
    XLSX.writeFile(workbook, "activities.xlsx");
  };

  const navigateToNewPage = (activityId) => {
    window.open(`/JoinPage?aid=${activityId}`, "_blank"); // 在新視窗打開，_blank 表示新視窗
  };

  const handleParticipationClick = async (activity) => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }
  
    const decodedToken = jwtDecode(token);
    const { e_id } = decodedToken;
  
    try {
      const status = participationStatus[activity.a_id]; 
      const isRegistered = status === 0; 
  
      if (status === 1) {
        // 狀態為請假，改成已報名
        await axios.put('/participation-update-status', {
          e_id,
          a_id: activity.a_id,
          j_status: 0,
          editor: e_id,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setParticipationStatus((prev) => ({ ...prev, [activity.a_id]: 0 }));
        return;
      }
  
      // 狀態為未報名
      if (status === null || status === undefined) {
        const questionsResponse = await axios.get(`/questions/${activity.a_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const questions = questionsResponse.data;
  
        // 先完成報名動作
        await axios.post('/participation-register', {
          e_id,
          a_id: activity.a_id,
          j_status: 0, // 0表示報名
          editor: e_id,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setParticipationStatus((prev) => ({ ...prev, [activity.a_id]: 0 }));
  
        // 報名成功後再檢查問題表單，如有問題才顯示
        if (questions.length > 0) {
          // 報名後再次取得 responses，此時應該是空的
          const responsesResponse = await axios.get(`/responses/${activity.a_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const existingResponses = responsesResponse.data;
  
          const initialResponses = {};
          // questions.forEach((q) => {
          //   const existingResponse = existingResponses.find((r) => r.q_id === q.q_id);
          //   if (q.q_type === 1) {
          //     initialResponses[q.q_id] = existingResponse ? existingResponse.r_text : "";
          //   } else {
          //     // 選擇題(單選)預設值若無回答則為空字串
          //     initialResponses[q.q_id] = existingResponse ? String(existingResponse.o_id) : "";
          //   }
          // });
  
          setResponses(initialResponses);
          setActivityQuestions(questions);
          setSelectedActivityForQuestions(activity);
          setShowQuestionsModal(true);
        } else {
          // 沒有問題表單，使用者已經是已報名狀態
          // 此情況下無需再次顯示 Modal
        }
  
      } else if (status === 0) {
        // 已報名 -> 轉請假
        await axios.put('/participation-update-status', {
          e_id,
          a_id: activity.a_id,
          j_status: 1,
          editor: e_id,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setParticipationStatus((prev) => ({ ...prev, [activity.a_id]: 1 }));
      }
    } catch (error) {
      // Error handling participation
    }
  };

  // 直接請假（未報名狀態下直接標記為請假）
  const handleDirectLeave = async (activity) => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    const decodedToken = jwtDecode(token);
    const { e_id } = decodedToken;

    try {
      const status = participationStatus[activity.a_id];

      if (status === null || status === undefined) {
        // 未報名狀態 -> 直接請假
        await axios.post('/participation-register', {
          e_id,
          a_id: activity.a_id,
          j_status: 1, // 1 表示請假
          editor: e_id,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setParticipationStatus((prev) => ({ ...prev, [activity.a_id]: 1 }));
      } else if (status === 0) {
        // 已報名 -> 轉請假
        await axios.put('/participation-update-status', {
          e_id,
          a_id: activity.a_id,
          j_status: 1,
          editor: e_id,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setParticipationStatus((prev) => ({ ...prev, [activity.a_id]: 1 }));
      }
    } catch (error) {
      console.error('請假操作失敗:', error);
    }
  };
  
  
  
  

  // 新增：當使用者點擊「報名資訊」時先檢查問題
  const handleShowQuestions = async (activity) => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }
  
    try {
      // 1. 獲取問題資料
      const questionsResponse = await axios.get(`/questions/${activity.a_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const questions = questionsResponse.data;
      if (questions.length === 0) {
        // 無問題，直接開啟報名資訊頁面
        navigateToNewPage(activity.a_id);
        return;
      }
  
      // 2. 獲取使用者的回答資料
      const responsesResponse = await axios.get(`/responses/${activity.a_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const existingResponses = responsesResponse.data;
  
      // 3. 初始化 responses 狀態（包含已有回答）
      const initialResponses = {};
      questions.forEach((q) => {
        const existingResponse = existingResponses.find((r) => r.q_id === q.q_id);
        if (q.q_type === 1) {
          // 問答題
          initialResponses[q.q_id] = existingResponse ? existingResponse.r_text : "";
        } else {
          // 選擇題 (單選)
          initialResponses[q.q_id] = existingResponse ? String(existingResponse.o_id) : "";
        }
      });
  
      setResponses(initialResponses);
      setActivityQuestions(questions);
      setSelectedActivityForQuestions(activity);
      setShowQuestionsModal(true);
    } catch (error) {
      navigateToNewPage(activity.a_id); // 若出錯，仍可選擇開啟報名頁面
    }
  };
  

  const handleQuestionChange = (q_id, q_type, value) => {
    setResponses(prev => {
      const newResponses = { ...prev };
      if (q_type === 1) {
        newResponses[q_id] = value;
      } else {
        // 選擇題
        newResponses[q_id] = value;
      }
      return newResponses;
    });
  };
  

  const handleSubmitQuestions = async () => {
    if(!selectedActivityForQuestions || activityQuestions.length === 0) {
      setShowQuestionsModal(false);
      return;
    }
  
    const token = localStorage.getItem("token");
    if(!token) {
      return;
    }
  
    const decodedToken = jwtDecode(token);
    const { e_id } = decodedToken;
  
    try {
      // 組合所有回答為一個陣列
      const answers = activityQuestions.map((q) => {
        const qType = Number(q.q_type);
        const answerValue = responses[q.q_id];
        if (qType === 1) {
          // 問答題
          return { q_id: q.q_id, r_text: answerValue };
        } else {
          // 選擇題 (單選)
          return { q_id: q.q_id, o_id: answerValue };
        }
      });
  
      // 一次性傳送所有回答給後端
      await axios.post('/responses/bulk', {
        a_id: selectedActivityForQuestions.a_id,
        e_id,
        answers
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      setShowQuestionsModal(false);
      //navigateToNewPage(selectedActivityForQuestions.a_id);
      //navigate("/PjoinPage"); // 導向到 PjoinPage 頁面
      //navigateToNewPage(activity.a_id);
    } catch (error) {
      // Error submitting responses
    }
  };
  
  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>載入中...</p>
      </Container>
    );
  }

  if (activities.length === 0) {
    return (
      <Container>
        <h2 className="my-4">目前活動</h2>
        <p>目前沒有符合條件的活動資料。</p>
      </Container>
    );
  }

  return (
    <Container>
    <h2 className="my-4">目前活動</h2>
    <Button variant="primary" className="mb-3" onClick={handleExport}>
      匯出 Excel
    </Button>
    <Row>
      {activities.map((activity) => {
        const status = participationStatus[activity.a_id];
        return (
          <Col key={activity.a_id} md={6} lg={4} className="mb-4">
            <Card
              className={
                status === 0
                  ? "bg-info text-white shadow-sm"
                  : status === 1
                  ? "bg-danger text-white shadow-sm"
                  : "bg-light text-dark shadow-sm"
              }
            >
              <Card.Body className="d-flex flex-column">
                {/* 卡片標題 */}
                <Card.Title className="d-flex justify-content-between align-items-start">
                  <span className="fw-bold" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{activity.a_name}</span>
                  <Badge bg="secondary" className="flex-shrink-0 ms-2">{activity.store_name}</Badge>
                </Card.Title>
  
                {/* 主題與日期 */}
                <Card.Subtitle className="mt-2 fw-bold text-truncate">
                  主題: {activity.lecturer_topics}
                </Card.Subtitle>
                <Card.Text className="mt-2 mb-2">
                  日期: {activity.a_date}
                  <br />
                  負責人: {activity.employee_name}
                </Card.Text>

                {/* EDM 照片預覽 */}
    {activity.a_EDM && (
      <img
        src={`/uploads/AEDM/${activity.a_EDM}?t=${new Date().getTime()}`}
        alt="活動 EDM"
        style={{
          width: 80,
          height: 100,
          position: "absolute",
          bottom: 10,
          right: 10,
          borderRadius: 8,
          cursor: "pointer",
        }}
        onClick={() => {
          setLightboxImage(`/uploads/AEDM/${activity.a_EDM}?t=${new Date().getTime()}`);
          setIsLightboxOpen(true);
        }}
      />
    )}
  
                {/* 報名狀態 */}
                <Card.Text className="mt-auto">
                  報名狀態:{" "}
                  <Badge bg={status === 0 ? "success" : status === 1 ? "warning" : "secondary"}>
                    {status === 0
                      ? "已報名"
                      : status === 1
                      ? "請假"
                      : "未報名"}
                  </Badge>
                </Card.Text>
  
                {/* 按鈕區域 */}
                <div className="d-flex justify-content-start gap-2 flex-wrap">
                  {/* 活動已停止異動提示 */}
                  {activity.a_stop === 1 ? (
                    <Badge bg="secondary" className="py-2 px-3">活動禁止異動</Badge>
                  ) : (
                  <>
                  {/* 未報名狀態：顯示報名和請假兩個按鈕 */}
                  {(status === null || status === undefined) && (
                    <>
                      <Button
                        variant="primary"
                        onClick={() => handleParticipationClick(activity)}
                      >
                        報名
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDirectLeave(activity)}
                      >
                        請假
                      </Button>
                    </>
                  )}
                  {/* 已報名狀態：顯示請假按鈕 */}
                  {status === 0 && (
                    <Button
                      variant="danger"
                      onClick={() => handleDirectLeave(activity)}
                    >
                      請假
                    </Button>
                  )}
                  {/* 請假狀態：顯示報名按鈕 */}
                  {status === 1 && (
                    <Button
                      variant="primary"
                      onClick={() => handleParticipationClick(activity)}
                    >
                      報名
                    </Button>
                  )}
                  </>
                  )}
                  <Button
                    variant="warning"
                    onClick={() => navigateToNewPage(activity.a_id)}
                    className="text-white"
                  >
                    查看
                  </Button>
                  {Boolean(activity.hasQuestions) && (
                    <Button
                      variant="success"
                      onClick={() => handleShowQuestions(activity)}
                    >
                      問題
                    </Button>
                  )}
                  <Button
                    variant="link"
                    onClick={() => toggleExpand(activity.a_id)}
                    aria-controls={`collapse-${activity.a_id}`}
                    aria-expanded={expanded[activity.a_id] || false}
                    className="text-black"
                  >
                    {expanded[activity.a_id] ? <FaChevronUp /> : <FaChevronDown />} 詳細資訊
                  </Button>
                </div>
  
                {/* 詳細資訊 Collapse */}
                <Collapse in={expanded[activity.a_id]}>
                  <div id={`collapse-${activity.a_id}`} className="mt-3">
                    <Card.Text>活動類別: {activity.mainact_name}</Card.Text>
                    <Card.Text>講師與主題: {activity.lecturer_topics}</Card.Text>
                    <Card.Text>參訪公司: {activity.company_names}</Card.Text>
                    <Card.Text>
                      地點: {activity.a_location ? (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.a_location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#17a2b8' }}
                        >
                          {activity.a_location}
                        </a>
                      ) : '-'}
                    </Card.Text>
                    <Card.Text>預算: {activity.a_budget}</Card.Text>
                    <Card.Text>備註: {activity.a_remark}</Card.Text>
                    <Card.Text>
                      人數限: {activity.a_people} <FaUserFriends />
                    </Card.Text>
                  </div>
                </Collapse>
              </Card.Body>
            </Card>
          </Col>
        );
      })}
    </Row>
     {/* Lightbox 顯示 */}
     {isLightboxOpen && (
      <Lightbox
        mainSrc={lightboxImage}
        onCloseRequest={() => setIsLightboxOpen(false)}
      />
    )}
     {/* 問題填寫的 Modal */}
     <Modal show={showQuestionsModal} onHide={() => setShowQuestionsModal(false)}>
  <Modal.Header closeButton>
    <Modal.Title>請填寫以下問題</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    {activityQuestions.length === 0 ? (
      <p>目前沒有需填寫的問題。</p>
    ) : (
      activityQuestions.map((q) => {
        const qType = Number(q.q_type);
        return (
          <div key={q.q_id} className="mb-3">
            <p><strong>{q.q_text}</strong> ({qType === 1 ? "問答題" : "選擇題"})</p>
            {qType === 1 ? (
              <Form.Control
                type="text"
                value={responses[q.q_id] || ""}
                onChange={(e) => handleQuestionChange(q.q_id, qType, e.target.value)}
                placeholder="請輸入答案"
              />
            ) : (
              // 選擇題使用 Radio 按鈕
              <div>
                <Form.Label>請選擇一個選項：</Form.Label>
                {q.options.map((opt, i) => (
  <Form.Check
    key={opt.o_id || i}
    type="radio"
    name={`question-${q.q_id}`}
    id={`q${q.q_id}-option${opt.o_id}`}
    label={opt.o_text}
    value={opt.o_id}
    checked={responses[q.q_id] === String(opt.o_id)}
    onChange={(e) => handleQuestionChange(q.q_id, qType, e.target.value)}
  />
))}
              </div>
            )}
          </div>
        );
      })
    )}
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowQuestionsModal(false)}>
      取消
    </Button>
    <Button variant="primary" onClick={handleSubmitQuestions}>
      送出回答
    </Button>
  </Modal.Footer>
</Modal>
  </Container>
  
  );
};

export default PjoinPage;
